import type { AvailabilityPeriod, AvailabilityWeek, BookedTime, StaffAvailability, TimeOff } from "@vaulted/shared";
import { availabilityOn, cutOut, fromMinutes, periodApplies, shiftDate, timeOffOn, toMinutes } from "@vaulted/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState, type PointerEvent, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router";
import { ApiError, del, errorMessage, get, patch, post, put } from "../api";
import { Alert, Button, Checkbox, Dialog, Field, Loading, PageHeader, cx } from "../components/ui";
import { useSession } from "../session";
import { DateBar, dayLabel, todayIn } from "./Calendar";

const PX_PER_MIN = 0.9;
const SNAP = 15;
/** Space left above and below hours that reach or pass the edges of the clinic's setting (minutes). */
const EDGE_ROOM = 30;
/** Staff shown side by side under each day. */
const MAX_STAFF = 4;
/** Each person's colour (on their blocks' edge and their name). No greens, reds, or yellows: those mean available, off, and gaps. */
const STAFF_COLORS = ["#2563eb", "#c51a6a", "#7c3aed", "#ea580c", "#0891b2", "#475569"];
const DAY_EDGE = "border-l-2 border-l-ink-soft/40";
const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

const hourLabel = (m: number) => `${((Math.floor(m / 60) + 11) % 12) + 1} ${m < 720 || m === 1440 ? "AM" : "PM"}`;
/** "9:30 AM" from minutes after midnight. */
export const minLabel = (m: number) => {
  const h = Math.floor(m / 60) % 24;
  const mm = m % 60;
  return `${((h + 11) % 12) + 1}${mm ? `:${String(mm).padStart(2, "0")}` : ""} ${h < 12 ? "AM" : "PM"}`;
};
const timeLabel = (t: string) => minLabel(toMinutes(t));
const initials = (name: string) =>
  name
    .split(/\s+/)
    .map((p) => p[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

type Span = { start: number; end: number };
type Selection =
  | { kind: "new"; staff: StaffAvailability; date: string; start: number; end: number; offFirst?: boolean }
  | { kind: "weekly"; staff: StaffAvailability; date: string; period: AvailabilityPeriod }
  | { kind: "extra"; staff: StaffAvailability; date: string; id: string; start: string; end: string }
  | { kind: "timeOff"; staff: StaffAvailability; date: string; t: TimeOff }
  | { kind: "closure"; date: string };

function useNarrow() {
  const query = "(max-width: 767px)";
  const [narrow, setNarrow] = useState(() => typeof window !== "undefined" && window.matchMedia(query).matches);
  useEffect(() => {
    const m = window.matchMedia(query);
    const on = () => setNarrow(m.matches);
    m.addEventListener("change", on);
    return () => m.removeEventListener("change", on);
  }, []);
  return narrow;
}

/** The availability calendar: a week of hours and time off for up to four staff side by side (a day at a time on phones). */
export function AvailabilityPage() {
  const narrow = useNarrow();
  const [params, setParams] = useSearchParams();
  const [date, setDateState] = useState(params.get("date") ?? todayIn());
  const setDate = (d: string) => {
    setDateState(d);
    const next = new URLSearchParams(params);
    next.set("date", d);
    setParams(next, { replace: true });
  };
  const [tz, setTz] = useState<string | undefined>(undefined);
  const today = todayIn(tz);
  // Today first, then the next six days (one day on phones); past days aren't shown.
  const from = date < today ? today : date;
  const days = narrow ? 1 : 7;
  const data = useQuery({ queryKey: ["availability-week", from, days], queryFn: () => get<AvailabilityWeek>(`/api/availability/week?from=${from}&days=${days}`) });
  const [picked, setPicked] = useState<string[] | null>(null);
  const [selection, setSelection] = useState<Selection | null>(null);
  const w = data.data;
  useEffect(() => {
    if (w?.timezone && w.timezone !== tz) setTz(w.timezone);
  }, [w?.timezone, tz]);
  const savePeople = useMutation({ mutationFn: (staffIds: string[]) => put<{ ok: true }>("/api/availability/people", { staffIds }) });

  // Who's shown: always yourself (when you take appointments), then the people you picked (saved to your account),
  // or until you've picked anyone, whoever has hours, time off, or bookings this week.
  const others = picked ?? w?.myPeople ?? null;
  const shown = useMemo(() => {
    if (!w) return [];
    const me = w.staff.filter((s) => s.staffId === w.me);
    const rest = w.staff.filter((s) => s.staffId !== w.me);
    if (others) return [...me, ...rest.filter((s) => others.includes(s.staffId))].slice(0, MAX_STAFF);
    if (narrow && me.length) return me;
    const busy = (s: StaffAvailability) => s.periods.length + s.extras.length + s.timeOff.length + (s.busy?.length ?? 0) > 0 || w.booked.some((b) => b.staffId === s.staffId);
    return [...me, ...rest.filter(busy), ...rest.filter((s) => !busy(s))].slice(0, MAX_STAFF);
  }, [w, others, narrow]);
  const color = (id: string) => w?.staff.find((s) => s.staffId === id)?.color ?? STAFF_COLORS[Math.max(0, w?.staff.findIndex((s) => s.staffId === id) ?? 0) % STAFF_COLORS.length]!;
  const togglePick = (id: string) => {
    if (!w || id === w.me) return;
    const cur = shown.map((s) => s.staffId).filter((x) => x !== w.me);
    const room = MAX_STAFF - (w.staff.some((s) => s.staffId === w.me) ? 1 : 0);
    const next = cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id].slice(-room);
    setPicked(next);
    savePeople.mutate(next);
  };
  const canEdit = (staffId: string) => !!w && (w.canManage || (w.canEditOwn && staffId === w.me));
  const label = narrow ? dayLabel(from) : `${dayLabel(from, { month: "short", day: "numeric" })} – ${dayLabel(shiftDate(from, 6), { month: "short", day: "numeric", year: "numeric" })}`;
  const back = shiftDate(from, -days);

  return (
    <div>
      <PageHeader title="Availability" description="When each person can be booked, and their time off. Drag in someone's column to add hours, or across their hours to take time off (a doctor's appointment); click a block to change it." />
      <div className="mb-4 flex flex-col gap-3">
        <DateBar
          date={from}
          min={today}
          label={label}
          onPrev={() => setDate(back < today ? today : back)}
          onToday={() => setDate(today)}
          onNext={() => setDate(shiftDate(from, days))}
          onPick={(d) => setDate(d < today ? today : d)}
        />
        {w && w.staff.length > 1 && (
          <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label={`Who to show (up to ${MAX_STAFF})`}>
            <span className="mr-1 text-sm text-muted">Showing</span>
            {w.staff.map((s) => {
              const on = shown.some((x) => x.staffId === s.staffId);
              const mine = s.staffId === w.me;
              return (
                <button
                  key={s.staffId}
                  type="button"
                  aria-pressed={on}
                  disabled={mine}
                  title={mine ? "You're always shown" : undefined}
                  onClick={() => togglePick(s.staffId)}
                  className={cx("flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-sm", on ? "border-ink-soft bg-white font-semibold" : "border-line text-muted hover:border-ink-soft")}
                >
                  <span className="size-2.5 rounded-full" style={{ background: on ? color(s.staffId) : "var(--color-line)" }} />
                  {s.name}
                </button>
              );
            })}
          </div>
        )}
      </div>
      {data.isLoading ? (
        <Loading />
      ) : data.error || !w ? (
        <Alert>{errorMessage(data.error)}</Alert>
      ) : w.staff.length === 0 ? (
        <Alert tone="info">{w.seesTeam ? "Nobody takes appointments yet. Staff show here once their role can perform a service (Catalog → a service → who can perform it)." : "You don't take appointments, so you have no hours to set."}</Alert>
      ) : shown.length === 0 ? (
        <Alert tone="info">Pick someone to show.</Alert>
      ) : (
        <Grid w={w} staff={shown} today={today} color={color} canEdit={canEdit} onSelect={setSelection} />
      )}
      <Legend showCoverage={!!w?.seesTeam} />
      {selection && w && <SelectionDialog w={w} sel={selection} today={today} canEdit={selection.kind === "closure" ? w.canManage : canEdit(selection.staff.staffId)} onClose={() => setSelection(null)} />}
    </div>
  );
}

function Legend({ showCoverage }: { showCoverage: boolean }) {
  const item = (swatch: ReactNode, text: string) => (
    <span className="flex items-center gap-1.5">
      {swatch}
      {text}
    </span>
  );
  const box = (style: React.CSSProperties) => <span className="inline-block h-3 w-4 rounded-sm border" style={style} />;
  return (
    <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted">
      {item(box({ background: "var(--color-ok-wash)", borderColor: "var(--color-ok)" }), "Available")}
      {item(box({ background: "white", borderColor: "var(--color-line)", borderLeft: "4px solid #2563eb" }), "Edge colour: whose it is")}
      {item(box({ background: "var(--color-danger-wash)", borderColor: "var(--color-danger)" }), "Time off")}
      {item(box({ background: "white", borderColor: "var(--color-ink-soft)", borderStyle: "dashed" }), "Booked")}
      {showCoverage && item(box({ background: GAP, borderColor: "#e0b52c" }), "Clinic open, nobody available")}
      {item(box({ background: CLOSED, borderColor: "var(--color-line)" }), "Clinic closed")}
    </div>
  );
}

const GAP = "#fcd96a";
const CLOSED = "repeating-linear-gradient(45deg, var(--color-mist), var(--color-mist) 5px, var(--color-line) 5px, var(--color-line) 7px)";

function Grid({
  w,
  staff,
  today,
  color,
  canEdit,
  onSelect,
}: {
  w: AvailabilityWeek;
  staff: StaffAvailability[];
  today: string;
  color: (id: string) => string;
  canEdit: (staffId: string) => boolean;
  onSelect: (s: Selection) => void;
}) {
  const dates = w.openHours.map((o) => o.date);
  // Hours shown: the clinic's setting, stretched with half an hour to spare when anything reaches or passes its edges.
  const edges: number[] = [];
  for (const d of dates) {
    for (const s of staff) for (const p of availabilityOn(s, d)) edges.push(p.start, p.end);
    for (const b of w.booked) if (b.date === d && staff.some((s) => s.staffId === b.staffId)) edges.push(b.start, b.end);
  }
  const earliest = Math.min(...edges, Infinity);
  const latest = Math.max(...edges, -Infinity);
  const dayStart = Math.max(0, earliest <= w.viewStart ? Math.floor((earliest - EDGE_ROOM) / 30) * 30 : w.viewStart);
  const dayEnd = Math.min(1440, latest >= w.viewEnd ? Math.ceil((latest + EDGE_ROOM) / 30) * 30 : w.viewEnd);
  const height = (dayEnd - dayStart) * PX_PER_MIN;
  const y = (m: number) => (Math.max(dayStart, Math.min(dayEnd, m)) - dayStart) * PX_PER_MIN;
  const h = (a: number, b: number) => Math.max(y(b) - y(a), 0);
  // Hour lines on the hour, even when the view starts or ends on a half hour.
  const hours = Array.from({ length: Math.floor((dayEnd - Math.ceil(dayStart / 60) * 60) / 60) + 1 }, (_, i) => Math.ceil(dayStart / 60) * 60 + i * 60).filter((m) => m < dayEnd);
  const [drag, setDrag] = useState<{ staffId: string; date: string; from: number; to: number; onHours: boolean } | null>(null);
  // After dragging across hours, the click that follows isn't a click on them.
  const skipClick = useRef(false);
  const openUnlessDragged = (fn: () => void) => () => {
    if (skipClick.current) skipClick.current = false;
    else fn();
  };
  const minuteAt = (e: PointerEvent<HTMLElement>) => {
    const top = e.currentTarget.getBoundingClientRect().top;
    return Math.max(dayStart, Math.min(dayEnd, Math.round((dayStart + (e.clientY - top) / PX_PER_MIN) / SNAP) * SNAP));
  };
  const cols = staff.length + (w.seesTeam ? 1 : 0);
  const template = `3rem ${dates.map(() => `${w.seesTeam ? "0.6rem " : ""}repeat(${staff.length}, minmax(${dates.length === 1 ? "4.5rem" : "1.9rem"}, 1fr))`).join(" ")}`;

  return (
    <div className="overflow-x-auto rounded-[var(--radius-panel)] border border-line bg-white">
      <div className="grid min-w-fit select-none" style={{ gridTemplateColumns: template }}>
        {/* Day headers */}
        <div className="sticky left-0 z-20 border-b border-line bg-white" />
        {dates.map((d) => {
          const closure = w.closures.find((c) => c.date === d);
          const head = (
            <>
              <div className={cx("truncate text-sm font-semibold", d === today && "text-brand")}>{dayLabel(d, { weekday: "short", month: "numeric", day: "numeric" })}</div>
              {closure && <div className="truncate text-[11px] text-muted">Closed{closure.start !== null ? ` ${minLabel(closure.start)}–${minLabel(closure.end!)}` : ""}{closure.label ? `: ${closure.label}` : ""}</div>}
            </>
          );
          return (
            <div key={d} className={cx("border-b border-line px-1.5 py-1.5 text-center", DAY_EDGE)} style={{ gridColumn: `span ${cols}` }}>
              {w.canManage && d >= today ? (
                <button type="button" className="w-full rounded hover:bg-mist" title={closure ? "Clinic closed: change or reopen" : "Close the clinic this day"} onClick={() => onSelect({ kind: "closure", date: d })}>
                  {head}
                </button>
              ) : (
                head
              )}
            </div>
          );
        })}
        {/* Who's in each column */}
        <div className="sticky left-0 z-20 border-b border-line bg-white" />
        {dates.map((d) => (
          <div key={d} className="contents">
            {w.seesTeam && <div className={cx("border-b border-line", DAY_EDGE)} title="Clinic" />}
            {staff.map((s, i) => (
              <div
                key={s.staffId}
                className={cx("truncate border-b-[3px] px-0.5 py-1 text-center text-[11px] font-semibold", i === 0 && !w.seesTeam && DAY_EDGE)}
                style={{ color: color(s.staffId), borderBottomColor: color(s.staffId) }}
                title={s.name}
              >
                {dates.length === 1 ? s.name : staff.length <= 3 ? s.name.split(/\s+/)[0] : initials(s.name)}
              </div>
            ))}
          </div>
        ))}
        {/* Hours */}
        <div className="sticky left-0 z-20 bg-white" style={{ height }}>
          {hours.map((m) => (
            <div key={m} className="absolute right-1 -translate-y-1/2 text-[10px] text-muted" style={{ top: y(m) }}>
              {m > dayStart && hourLabel(m)}
            </div>
          ))}
        </div>
        {dates.map((d) => {
          const open = w.openHours.find((o) => o.date === d)?.periods ?? [];
          const closure = w.closures.find((c) => c.date === d);
          // Clinic open but nobody (anyone, not just those shown) available.
          // Busy time on their own calendars counts as away, as it does for booking.
          const covering = w.staff.flatMap((s) => availabilityOn({ ...s, timeOff: [...s.timeOff, ...(s.busy ?? [])] }, d));
          const gaps = cutOut(open, covering);
          const past = d < today;
          return (
            <div key={d} className="contents">
              {w.seesTeam && (
                <div className={cx("relative bg-mist/60", DAY_EDGE)} style={{ height }} title="Clinic hours">
                  {open.map((p, i) => (
                    <div key={i} className="absolute inset-x-0 bg-white" style={{ top: y(p.start), height: h(p.start, p.end) }} />
                  ))}
                  {closure && <div className="absolute inset-x-0" style={{ top: y(closure.start ?? 0), height: h(closure.start ?? 0, closure.end ?? 1440), background: CLOSED }} title={`Closed${closure.label ? `: ${closure.label}` : ""}`} />}
                  {gaps.map((g, i) => (
                    <div key={i} className="absolute inset-x-0" style={{ top: y(g.start), height: h(g.start, g.end), background: GAP }} title={`Nobody available ${minLabel(g.start)}–${minLabel(g.end)}`} />
                  ))}
                </div>
              )}
              {staff.map((s, i) => {
                const editable = canEdit(s.staffId) && !past;
                const weekly = s.periods.filter((p) => periodApplies(p, d));
                const extras = s.extras.filter((x) => x.date === d);
                const offs = s.timeOff.map((t) => ({ t, span: timeOffOn(t, d) })).filter((x): x is { t: TimeOff; span: Span } => x.span !== null);
                const busy = (s.busy ?? []).map((t) => ({ t, span: timeOffOn(t, d) })).filter((x): x is { t: TimeOff; span: Span } => x.span !== null);
                const booked = w.booked.filter((b) => b.staffId === s.staffId && b.date === d);
                const dragging = drag && drag.staffId === s.staffId && drag.date === d ? { start: Math.min(drag.from, drag.to), end: Math.max(drag.from, drag.to) } : null;
                return (
                  <div
                    key={s.staffId}
                    className={cx("relative bg-mist/50", i === 0 && !w.seesTeam && DAY_EDGE, i > 0 && "border-l border-line/70", past && "opacity-60", editable && "cursor-cell")}
                    style={{ height }}
                    onPointerDown={(e) => {
                      // Drag on empty space, or across hours (e.g. time off in the middle of a shift); a click on hours opens them.
                      const onHours = (e.target as HTMLElement).closest("[data-hours]");
                      if (!editable || (e.target !== e.currentTarget && !onHours)) return;
                      if (!onHours) e.currentTarget.setPointerCapture(e.pointerId);
                      const m = minuteAt(e);
                      setDrag({ staffId: s.staffId, date: d, from: m, to: m, onHours: !!onHours });
                    }}
                    onPointerMove={(e) => drag && drag.staffId === s.staffId && drag.date === d && setDrag({ ...drag, to: minuteAt(e) })}
                    onPointerUp={() => {
                      if (!drag || drag.staffId !== s.staffId || drag.date !== d) return;
                      const start = Math.min(drag.from, drag.to);
                      const end = Math.max(drag.from, drag.to);
                      setDrag(null);
                      // A plain click on hours is left to the hours themselves (change or remove them).
                      if (drag.onHours && end - start < SNAP) return;
                      if (drag.onHours) skipClick.current = true;
                      const sel = end - start >= SNAP ? { start, end } : { start: Math.min(start, 1440 - 60), end: Math.min(start, 1440 - 60) + 60 };
                      // Inside their hours, they're most likely taking time off.
                      const inHours = availabilityOn(s, d).some((p) => p.start <= sel.start && p.end >= sel.end);
                      onSelect({ kind: "new", staff: s, date: d, ...sel, offFirst: inHours });
                    }}
                  >
                    {hours.map((m) => (
                      <div key={m} className="pointer-events-none absolute inset-x-0 border-t border-line/60" style={{ top: y(m) }} />
                    ))}
                    {weekly.map((p) => (
                      <Block
                        key={p.id}
                        tone="ok"
                        accent={color(s.staffId)}
                        top={y(toMinutes(p.start))}
                        height={h(toMinutes(p.start), toMinutes(p.end))}
                        title={`${s.name}: ${timeLabel(p.start)}–${timeLabel(p.end)} (every ${WEEKDAYS[p.weekday]})`}
                        text={dates.length === 1 ? `${timeLabel(p.start)}–${timeLabel(p.end)}` : ""}
                        repeat={dates.length === 1}
                        hours
                        onClick={openUnlessDragged(() => onSelect({ kind: "weekly", staff: s, date: d, period: p }))}
                      />
                    ))}
                    {extras.map((x) => (
                      <Block
                        key={x.id}
                        tone="ok"
                        accent={color(s.staffId)}
                        top={y(toMinutes(x.start))}
                        height={h(toMinutes(x.start), toMinutes(x.end))}
                        title={`${s.name}: ${timeLabel(x.start)}–${timeLabel(x.end)} (this day)`}
                        text={dates.length === 1 ? `${timeLabel(x.start)}–${timeLabel(x.end)}` : ""}
                        hours
                        onClick={openUnlessDragged(() => onSelect({ kind: "extra", staff: s, date: d, id: x.id, start: x.start, end: x.end }))}
                      />
                    ))}
                    {offs.map(({ t, span }) => (
                      <Block key={t.id} tone="off" accent={color(s.staffId)} top={y(span.start)} height={h(span.start, span.end)} title={`${s.name}: time off${t.note ? ` (${t.note})` : ""}`} text={t.note || "Off"} onClick={() => onSelect({ kind: "timeOff", staff: s, date: d, t })} />
                    ))}
                    {busy.map(({ t, span }) => (
                      <Block
                        key={t.id}
                        tone="off"
                        accent={color(s.staffId)}
                        top={y(span.start)}
                        height={h(span.start, span.end)}
                        title={t.title ? `${t.title}: blocked ${minLabel(span.start)}–${minLabel(span.end)} (with the buffers before and after; change it on your calendar)` : `${s.name}: busy on their own calendar (change it there)`}
                        text={t.title || "Busy"}
                        onClick={() => {}}
                      />
                    ))}
                    {booked.map((b, j) => (
                      <BookedBlock key={`${b.id}-${j}`} b={b} top={y(b.start)} height={h(b.start, b.end)} />
                    ))}
                    {dragging && <div className="pointer-events-none absolute inset-x-0.5 z-10 rounded border-2 border-brand bg-brand-wash/60" style={{ top: y(dragging.start), height: Math.max(h(dragging.start, dragging.end), 4) }} />}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Available (green) or time off (red); the left edge is the person's colour. */
function Block({ tone, accent, top, height, title, text, repeat, hours, onClick }: { tone: "ok" | "off"; accent: string; top: number; height: number; title: string; text: string; repeat?: boolean; hours?: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      data-hours={hours ? "1" : undefined}
      onClick={onClick}
      title={title}
      aria-label={title}
      className={cx(
        // Fixed layers (hours at the back, then time off and busy, then visits): hovering never hides what's on top.
        "absolute inset-x-1 overflow-hidden rounded border border-l-4 px-1 text-left text-[10px] leading-tight hover:shadow",
        tone === "ok" ? "z-0 border-ok/30 bg-ok-wash text-ok" : "z-[1] border-danger/40 bg-danger-wash text-danger",
      )}
      style={{ top, height: Math.max(height - 1, 6), borderLeftColor: accent }}
    >
      {height >= 14 && (
        <span className="block truncate">
          {repeat && "↻ "}
          {text}
        </span>
      )}
    </button>
  );
}

function BookedBlock({ b, top, height }: { b: BookedTime & { appointmentId: string | null }; top: number; height: number }) {
  const { can } = useSession();
  const title = `${b.label}, ${minLabel(b.start)}–${minLabel(b.end)}`;
  const cls = "absolute inset-x-1.5 z-[2] overflow-hidden rounded border border-dashed border-ink-soft bg-white/90 px-0.5 text-[10px] leading-tight text-ink-soft";
  const style = { top, height: Math.max(height - 1, 6) };
  const text = height >= 14 && <span className="block truncate">{b.label}</span>;
  return can("appointments.manage") ? (
    <Link to={`/calendar?date=${b.date}`} className={cx(cls, "hover:border-ink")} style={style} title={`${title} (open the calendar)`}>
      {text}
    </Link>
  ) : (
    <div className={cx(cls, "pointer-events-none")} style={style} title={title}>
      {text}
    </div>
  );
}

// ── Dialogs ─────────────────────────────────────────────────────────────

function TimeInput({ value, onChange, label }: { value: string; onChange: (t: string) => void; label: string }) {
  return (
    <label className="flex flex-col gap-1 text-sm font-semibold text-ink-soft">
      {label}
      <input type="time" step={900} required className="rounded-[var(--radius-control)] border border-line bg-white px-2 py-1.5 text-[15px] font-normal text-ink" value={value === "24:00" ? "00:00" : value} onChange={(e) => onChange(e.target.value)} />
    </label>
  );
}

/** Midnight as an end time is the end of the day. */
const endTime = (t: string) => (t === "00:00" ? "24:00" : t);

/** An error from the server, with the visits that are in the way when there are some. */
function ErrorBox({ error }: { error: unknown }) {
  if (!error) return null;
  const visits = error instanceof ApiError ? ((error.details as { visits?: { id: string; date: string; start: string; label: string }[] } | undefined)?.visits ?? []) : [];
  if (!visits.length) return <Alert>{errorMessage(error)}</Alert>;
  return (
    <Alert>
      <p className="font-semibold">Something is booked then. Move it to another nurse or time first:</p>
      <ul className="mt-1 list-disc pl-5">
        {visits.slice(0, 8).map((v, i) => (
          <li key={`${v.id}-${i}`}>
            <Link className="underline" to={`/calendar?date=${v.date}`}>
              {v.label}, {dayLabel(v.date, { weekday: "short", month: "short", day: "numeric" })} at {timeLabel(v.start)}
            </Link>
          </li>
        ))}
      </ul>
    </Alert>
  );
}

function SelectionDialog({ w, sel, today, canEdit, onClose }: { w: AvailabilityWeek; sel: Selection; today: string; canEdit: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const done = async () => {
    await qc.invalidateQueries({ queryKey: ["availability-week"] });
    await qc.invalidateQueries({ queryKey: ["calendar"] });
    onClose();
  };
  if (sel.kind === "closure") return <ClosureDialog w={w} date={sel.date} onDone={done} onClose={onClose} />;
  if (sel.kind === "new") return <NewDialog sel={sel} onDone={done} onClose={onClose} />;
  if (sel.kind === "timeOff") return <TimeOffDialog sel={sel} canEdit={canEdit && sel.t.endDate >= today} onDone={done} onClose={onClose} />;
  return <HoursDialog sel={sel} canEdit={canEdit && sel.date >= today} onDone={done} onClose={onClose} />;
}

function NewDialog({ sel, onDone, onClose }: { sel: Extract<Selection, { kind: "new" }>; onDone: () => Promise<void>; onClose: () => void }) {
  const [kind, setKind] = useState<"hours" | "off">(sel.offFirst ? "off" : "hours");
  const [start, setStart] = useState(fromMinutes(sel.start));
  const [end, setEnd] = useState(fromMinutes(sel.end));
  const [repeat, setRepeat] = useState(true);
  const [until, setUntil] = useState("");
  const [allDay, setAllDay] = useState(false);
  const [endDate, setEndDate] = useState(sel.date);
  const [note, setNote] = useState("");
  const save = useMutation({
    mutationFn: () =>
      kind === "hours"
        ? post(`/api/availability/${sel.staff.staffId}/hours`, { date: sel.date, start, end: endTime(end), repeat, until: repeat && until ? until : null })
        : post(`/api/availability/${sel.staff.staffId}/time-off`, { startDate: sel.date, start: allDay ? null : start, endDate, end: allDay ? null : endTime(end), note }),
    onSuccess: onDone,
  });
  const day = WEEKDAYS[new Date(`${sel.date}T12:00:00Z`).getUTCDay()];
  return (
    <Dialog
      title={`${sel.staff.name}, ${dayLabel(sel.date)}`}
      onClose={onClose}
      footer={
        <>
          <Button variant="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" busy={save.isPending} onClick={() => save.mutate()}>
            {kind === "hours" ? "Add hours" : "Add time off"}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex gap-1 rounded-[var(--radius-control)] bg-mist p-1" role="radiogroup" aria-label="Add">
          {(
            [
              ["hours", "Available"],
              ["off", "Time off"],
            ] as const
          ).map(([k, text]) => (
            <button key={k} type="button" role="radio" aria-checked={kind === k} onClick={() => setKind(k)} className={cx("flex-1 rounded-md px-3 py-1.5 text-sm font-semibold", kind === k ? (k === "hours" ? "bg-white text-ok shadow-sm" : "bg-white text-danger shadow-sm") : "text-muted")}>
              {text}
            </button>
          ))}
        </div>
        {kind === "hours" ? (
          <>
            <div className="flex flex-wrap items-end gap-3">
              <TimeInput label="From" value={start} onChange={setStart} />
              <TimeInput label="Until" value={end} onChange={setEnd} />
            </div>
            <Checkbox label={`Repeat every ${day}`} description="Starting this date. Untick for this day only." checked={repeat} onChange={setRepeat} />
            {repeat && <Field label="Last week (optional)" type="date" min={sel.date} value={until} onChange={(e) => setUntil(e.target.value)} className="ml-9 max-w-56" hint="Leave empty to keep going." />}
          </>
        ) : (
          <>
            <Checkbox label="All day" checked={allDay} onChange={setAllDay} />
            <div className="flex flex-wrap items-end gap-3">
              {!allDay && <TimeInput label="From" value={start} onChange={setStart} />}
              <Field label="Through" type="date" min={sel.date} value={endDate} onChange={(e) => setEndDate(e.target.value || sel.date)} className="max-w-48" />
              {!allDay && <TimeInput label="Until" value={end} onChange={setEnd} />}
            </div>
            <Field label="Note (optional)" maxLength={200} value={note} onChange={(e) => setNote(e.target.value)} hint="Only you and managers see it." />
          </>
        )}
        <ErrorBox error={save.error} />
      </div>
    </Dialog>
  );
}

function HoursDialog({ sel, canEdit, onDone, onClose }: { sel: Extract<Selection, { kind: "weekly" | "extra" }>; canEdit: boolean; onDone: () => Promise<void>; onClose: () => void }) {
  const orig = sel.kind === "weekly" ? sel.period : sel;
  const [start, setStart] = useState(orig.start);
  const [end, setEnd] = useState(orig.end);
  const [scope, setScope] = useState<"day" | "following">("following");
  const id = sel.kind === "weekly" ? sel.period.id : sel.id;
  const save = useMutation({
    mutationFn: (remove: boolean) => patch(`/api/availability/${sel.staff.staffId}/hours/${id}`, { date: sel.date, start, end: endTime(end), scope, remove }),
    onSuccess: onDone,
  });
  const p = sel.kind === "weekly" ? sel.period : null;
  const dayName = p ? WEEKDAYS[p.weekday] : "";
  return (
    <Dialog
      title={`${sel.staff.name}, ${dayLabel(sel.date)}`}
      onClose={onClose}
      footer={
        canEdit ? (
          <>
            <Button variant="danger" busy={save.isPending && save.variables === true} onClick={() => save.mutate(true)}>
              {p ? (scope === "day" ? "Take this day off" : "Remove from this week on") : "Remove"}
            </Button>
            <Button variant="primary" busy={save.isPending && save.variables === false} onClick={() => save.mutate(false)}>
              Save
            </Button>
          </>
        ) : (
          <Button onClick={onClose}>Close</Button>
        )
      }
    >
      <div className="flex flex-col gap-4">
        <p className="text-sm text-ink-soft">
          {timeLabel(orig.start)}–{timeLabel(orig.end)}
          {p ? `, every ${dayName} from ${dayLabel(p.startsOn, { month: "short", day: "numeric", year: "numeric" })}${p.endsOn ? ` through ${dayLabel(p.endsOn, { month: "short", day: "numeric", year: "numeric" })}` : ""}.` : ", this day only."}
        </p>
        {canEdit && (
          <>
            <div className="flex flex-wrap items-end gap-3">
              <TimeInput label="From" value={start} onChange={setStart} />
              <TimeInput label="Until" value={end} onChange={setEnd} />
            </div>
            <p className="text-sm text-muted">Time off in the middle (a doctor's appointment)? Close this and drag across that time on the calendar.</p>
            {p && (
              <fieldset className="flex flex-col gap-1">
                <legend className="mb-1 text-sm font-semibold text-ink-soft">Change</legend>
                <label className="flex items-center gap-2 text-sm">
                  <input type="radio" className="accent-[var(--color-brand)]" checked={scope === "day"} onChange={() => setScope("day")} />
                  Just {dayLabel(sel.date, { weekday: "long", month: "short", day: "numeric" })}
                  <span className="text-muted">(hours taken away show as time off)</span>
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input type="radio" className="accent-[var(--color-brand)]" checked={scope === "following"} onChange={() => setScope("following")} />
                  This and following {dayName}s
                </label>
              </fieldset>
            )}
          </>
        )}
        <ErrorBox error={save.error} />
      </div>
    </Dialog>
  );
}

function TimeOffDialog({ sel, canEdit, onDone, onClose }: { sel: Extract<Selection, { kind: "timeOff" }>; canEdit: boolean; onDone: () => Promise<void>; onClose: () => void }) {
  const t = sel.t;
  const remove = useMutation({ mutationFn: () => del(`/api/availability/${sel.staff.staffId}/time-off/${t.id}`), onSuccess: onDone });
  const when = (date: string, time: string, edge: "start" | "end") =>
    `${dayLabel(date, { weekday: "short", month: "short", day: "numeric" })}${(edge === "start" && time === "00:00") || (edge === "end" && time === "24:00") ? "" : ` ${timeLabel(time)}`}`;
  const allDays = t.start === "00:00" && t.end === "24:00";
  return (
    <Dialog
      title={`${sel.staff.name}: time off`}
      onClose={onClose}
      footer={
        canEdit ? (
          <Button variant="danger" busy={remove.isPending} onClick={() => remove.mutate()}>
            Remove time off
          </Button>
        ) : (
          <Button onClick={onClose}>Close</Button>
        )
      }
    >
      <div className="flex flex-col gap-3">
        <p>
          {t.startDate === t.endDate && allDays
            ? `${dayLabel(t.startDate)}, all day`
            : t.startDate === t.endDate
              ? `${dayLabel(t.startDate)}, ${timeLabel(t.start)}–${timeLabel(t.end)}`
              : `${when(t.startDate, t.start, "start")} through ${when(t.endDate, t.end, "end")}`}
        </p>
        {t.note && <p className="text-sm text-ink-soft">{t.note}</p>}
        <ErrorBox error={remove.error} />
      </div>
    </Dialog>
  );
}

function ClosureDialog({ w, date, onDone, onClose }: { w: AvailabilityWeek; date: string; onDone: () => Promise<void>; onClose: () => void }) {
  const closure = w.closures.find((c) => c.date === date);
  const [label, setLabel] = useState(closure?.label ?? "");
  const [allDay, setAllDay] = useState(closure ? closure.start === null : true);
  const [start, setStart] = useState(closure?.start != null ? fromMinutes(closure.start) : "12:00");
  const [end, setEnd] = useState(closure?.end != null ? fromMinutes(closure.end) : "17:00");
  const save = useMutation({ mutationFn: () => put(`/api/closures/${date}`, { label, start: allDay ? null : start, end: allDay ? null : endTime(end) }), onSuccess: onDone });
  const reopen = useMutation({ mutationFn: () => del(`/api/closures/${date}`), onSuccess: onDone });
  return (
    <Dialog
      title={`${closure ? "Clinic closed" : "Close the clinic"}: ${dayLabel(date)}`}
      onClose={onClose}
      footer={
        <>
          {closure && (
            <Button variant="danger" busy={reopen.isPending} onClick={() => reopen.mutate()}>
              Reopen
            </Button>
          )}
          <Button variant="primary" busy={save.isPending} onClick={() => save.mutate()}>
            {closure ? "Save" : "Close the clinic"}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <p className="text-sm text-ink-soft">No clinic visits can be booked while it's closed. Nobody's hours change, so mobile visits still work. Reopen to work in the clinic.</p>
        <Field label="Reason (optional)" maxLength={80} value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Thanksgiving" />
        <Checkbox label="All day" checked={allDay} onChange={setAllDay} />
        {!allDay && (
          <div className="flex gap-3">
            <TimeInput label="From" value={start} onChange={setStart} />
            <TimeInput label="Until" value={end} onChange={setEnd} />
          </div>
        )}
        <ErrorBox error={save.error ?? reopen.error} />
      </div>
    </Dialog>
  );
}
